import {Table, TablePaginationConfig} from 'antd';

import {appRoute} from '../../../../../../../app-route';
import {Text} from '../../../../../../../component/text/text';
import {NavigationLink} from '../../../../../../../layout/navigation-link/navigation-link';
import {PageCard} from '../../../../../../../layout/page-card/page-card';
import {Trend} from '../../../../../../../layout/trend/trend';
import {Locale} from '../../../../../../../provider/locale/localization';
import {
    ReviewAnalyticsAspectsHookType,
    ReviewAspectRecordType,
    ReviewAspectTableSortedColumnsEnum,
} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import * as styles from '../../../../../reviews-and-answers-statistics/average-response-time/catalogs-stats-table/catalogs-stats-table.scss';
import {useReviewsAnalysisFilter} from '../../../../reviews-analysis-hook';
import {keywordsAndTopicsPageSizeOptions} from '../../keywords-and-topics-page-const';

type PropsType = {
    reviewAnalyticsAspects: ReviewAnalyticsAspectsHookType;
};

export function AspectsTable({reviewAnalyticsAspects}: PropsType): JSX.Element {
    const {comparePeriodFilter} = useReviewsAnalysisFilter(false);

    const isCompareMode = Boolean(comparePeriodFilter.startDate);

    const {data, isLoading, pagination, sorter: tableSorter} = reviewAnalyticsAspects;

    const {setSorterOrder, getSortOrder} = tableSorter;

    const {total, pageSize, page, onChange: onPaginationChange} = pagination;

    return (
        <PageCard title={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TABLE_TITLE" />}>
            <Table<ReviewAspectRecordType>
                dataSource={data?.results || []}
                loading={isLoading}
                onChange={(_newPagination: TablePaginationConfig, _filters, sorter) => {
                    setSorterOrder(sorter);
                }}
                pagination={{
                    total,
                    pageSize,
                    pageSizeOptions: keywordsAndTopicsPageSizeOptions,
                    current: page,
                    size: 'default',
                    showSizeChanger: true,
                    onChange: onPaginationChange,
                    totalBoundaryShowSizeChanger: data?.count,
                    // eslint-disable-next-line react/no-unstable-nested-components
                    showTotal: (totalCount) => {
                        return (
                            <Text
                                large
                                stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TOTAL"
                                valueMap={{
                                    count: (
                                        <Text bolder key="count">
                                            {totalCount}
                                        </Text>
                                    ),
                                }}
                            />
                        );
                    },
                }}
                scroll={{x: true}}
                size="middle"
            >
                <Table.Column<ReviewAspectRecordType>
                    render={(_value, record) => {
                        return (
                            <NavigationLink
                                to={`${appRoute.reviewsManagementReviews.path}?ordering=-creation_date&tag-list=${record.ids}`}
                            >
                                {record.name}
                            </NavigationLink>
                        );
                    }}
                    title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NAME" />}
                />

                {isCompareMode ? (
                    <>
                        <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEGATIVE" />}>
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainNegativeReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.negativeReviews.value}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNegativeReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaNegativeReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.negativeReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaNegativeReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                className={styles.CatalogStatsTable_column}
                                dataIndex={ReviewAspectTableSortedColumnsEnum.compareNegativeReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.negativeReviews.comparedValue}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.compareNegativeReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>
                        <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEUTRAL" />}>
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainNeutralReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.neutralReviews.value}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNeutralReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaNeutralReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.neutralReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaNeutralReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                dataIndex={ReviewAspectTableSortedColumnsEnum.compareNeutralReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.neutralReviews.comparedValue}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.compareNeutralReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>

                        <Table.ColumnGroup title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__POSITIVE" />}>
                            <Table.Column<ReviewAspectRecordType>
                                align="right"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.mainPositiveReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.positiveReviews.value}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainPositiveReviews)}
                                sorter
                                title="A"
                            />

                            <Table.Column<ReviewAspectRecordType>
                                align="center"
                                dataIndex={ReviewAspectTableSortedColumnsEnum.deltaPositiveReviews}
                                render={(_value, record) => {
                                    return <Trend delta={record.positiveReviews.delta || 0} />;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.deltaPositiveReviews)}
                                sorter
                                title={
                                    <Locale stringKey="REVIEW_AND_ANSWER_STATISTICS__AVERAGE_RESPONSE_TIME__DELTA" />
                                }
                            />

                            <Table.Column<ReviewAspectRecordType>
                                dataIndex={ReviewAspectTableSortedColumnsEnum.comparePositiveReviews}
                                render={(_value, record) => {
                                    return <Text block>{record.positiveReviews.comparedValue}</Text>;
                                }}
                                sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.comparePositiveReviews)}
                                sorter
                                title="B"
                            />
                        </Table.ColumnGroup>
                    </>
                ) : (
                    <>
                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainNegativeReviews}
                            render={(_value, record) => {
                                return <Text block>{record.negativeReviews.value}</Text>;
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNegativeReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEGATIVE" />}
                        />

                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainNeutralReviews}
                            render={(_value, record) => {
                                return <Text block>{record.neutralReviews.value}</Text>;
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainNeutralReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__NEUTRAL" />}
                        />

                        <Table.Column<ReviewAspectRecordType>
                            align="center"
                            dataIndex={ReviewAspectTableSortedColumnsEnum.mainPositiveReviews}
                            render={(_value, record) => {
                                return <Text block>{record.positiveReviews.value}</Text>;
                            }}
                            sortOrder={getSortOrder(ReviewAspectTableSortedColumnsEnum.mainPositiveReviews)}
                            sorter
                            title={<Locale stringKey="REVIEWS_ANALYSIS__ASPECTS_TABLE__POSITIVE" />}
                        />
                    </>
                )}
            </Table>
        </PageCard>
    );
}
